import React, { useState } from 'react';
import { prepareChartData } from '../../utils/chartDataUtils';

const ResultsTable = ({ scenarioResults, scenarios, globalSettings, scenarioVisibility }) => {
  const [showMode, setShowMode] = useState('year'); // 'year' or 'scenario'
  const [yearFilter, setYearFilter] = useState('all'); // 'all', 'decade', or specific year

  // Helper function to format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get visible scenarios
  const visibleScenarios = scenarios.filter(s => s.visible);

  if (visibleScenarios.length === 0) {
    return (
      <div className="no-data-message">
        No data to display. Please make at least one scenario visible.
      </div>
    );
  }

  // Check if we have any results
  const hasResults = visibleScenarios.some(scenario => 
    scenarioResults && scenarioResults[scenario.id]
  );

  if (!hasResults) {
    return (
      <div className="no-data-message">
        No calculation results available yet. Please fill in scenario details.
      </div>
    );
  }

  // Year-centric view (years as rows, scenarios as columns)
  if (showMode === 'year') {
    // Use prepareChartData to get unified data structure
    const chartData = prepareChartData({
      visibleScenarios,
      calculationResults: { scenarioResults },
      scenarioVisibility,
      visibilitySettings: globalSettings,
    });

    // Filter years if needed
    let filteredChartData = chartData;
    if (yearFilter === 'decade') {
      filteredChartData = chartData.filter(row => row.year % 5 === 0);
    } else if (yearFilter !== 'all') {
      const specificYear = parseInt(yearFilter);
      filteredChartData = chartData.filter(row => row.year === specificYear);
    }

    if (!filteredChartData.length) {
      return <div className="no-data-message">No data to display. Please make at least one scenario visible and run calculations.</div>;
    }

    // Dynamically get all columns (except 'year')
    const allColumns = Object.keys(filteredChartData[0]).filter(key => key !== 'year');

    return (
      <div className="results-table-container">
        <div className="table-controls">
          <div className="view-selector">
            <label>View by:</label>
            <button 
              className={showMode === 'year' ? 'active' : ''} 
              onClick={() => setShowMode('year')}
            >
              Year
            </button>
            <button 
              className={showMode === 'scenario' ? 'active' : ''} 
              onClick={() => setShowMode('scenario')}
            >
              Scenario
            </button>
          </div>
          <div className="year-filter">
            <label htmlFor="year-filter">Show years:</label>
            <select 
              id="year-filter" 
              value={yearFilter} 
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="all">All years</option>
              <option value="decade">Every 5 years</option>
              {chartData.map(row => (
                <option key={row.year} value={row.year}>{row.year}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Year</th>
                {allColumns.map(col => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredChartData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.year}</td>
                  {allColumns.map(col => (
                    <td key={col}>
                      {typeof row[col] === 'number' ? formatCurrency(row[col]) : (row[col] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Scenario-centric view (scenarios as rows, data types as columns)
  // This view shows summary stats for each scenario
  return (
    <div className="results-table-container">
      <div className="table-controls">
        <div className="view-selector">
          <label>View by:</label>
          <button 
            className={showMode === 'year' ? 'active' : ''} 
            onClick={() => setShowMode('year')}
          >
            Year
          </button>
          <button 
            className={showMode === 'scenario' ? 'active' : ''} 
            onClick={() => setShowMode('scenario')}
          >
            Scenario
          </button>
        </div>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Retirement Year</th>
              <th>Years of Service</th>
              <th>Final Salary</th>
              <th>Initial Pension</th>
              <th>TSP at Retirement</th>
              <th>Total Retirement Income</th>
              {globalSettings.calculateNpv && (
                <th>Total NPV</th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleScenarios.map(scenario => {
              const result = scenarioResults ? scenarioResults[scenario.id] : null;
              if (!result) {
                return (
                  <tr key={scenario.id}>
                    <td style={{ color: scenario.color }}>{scenario.name}</td>
                    <td>{scenario.retirementYear}</td>
                    <td>{scenario.yearsOfService || 'N/A'}</td>
                    <td colSpan={globalSettings.calculateNpv ? 5 : 4}>No data available</td>
                  </tr>
                );
              }
              
              const { years, salaryData, pensionData, tspBalanceData, npvData } = result; // Corrected: was tspData
              
              // Find retirement year index
              const retirementYearIndex = years.indexOf(scenario.retirementYear);
              if (retirementYearIndex === -1) {
                return (
                  <tr key={scenario.id}>
                    <td style={{ color: scenario.color }}>{scenario.name}</td>
                    <td>{scenario.retirementYear}</td>
                    <td>{scenario.yearsOfService || 'N/A'}</td>
                    <td colSpan={globalSettings.calculateNpv ? 5 : 4}>Retirement year not in calculated data</td>
                  </tr>
                );
              }
              
              // Find values at retirement
              const finalSalaryIndex = Math.max(0, retirementYearIndex - 1);
              const finalSalary = salaryData[finalSalaryIndex] || 0;
              const initialPension = pensionData[retirementYearIndex] || 0;
              const tspAtRetirement = tspBalanceData[retirementYearIndex] || 0; // Corrected: was tspData
              
              // Calculate initial retirement income
              const tspWithdrawal = tspAtRetirement * (scenario.withdrawalRate / 100);
              const initialRetirementIncome = initialPension + tspWithdrawal;
              
              return (
                <tr key={scenario.id}>
                  <td style={{ color: scenario.color }}>{scenario.name}</td>
                  <td>{scenario.retirementYear}</td>
                  <td>{scenario.yearsOfService || 'N/A'}</td>
                  <td>{formatCurrency(finalSalary)}</td>
                  <td>{formatCurrency(initialPension)}</td>
                  <td>{formatCurrency(tspAtRetirement)}</td>
                  <td>{formatCurrency(initialRetirementIncome)}</td>
                  {globalSettings.calculateNpv && npvData && (
                    <td>{formatCurrency(npvData.totalNpv)}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResultsTable;